import { AnyAction } from "redux";

// is a function that takes in any function that returns an action
// stores its type, and allows us to match other actions of the same type
type Matchable<AC extends () => AnyAction> = AC & {
  // store the type of the action creator
  type: ReturnType<AC>["type"];
  // match other action creators of the same type
  match(action: AnyAction): action is ReturnType<AC>;
};

// override method to take any action creator with no arguments
export function withMatcher<AC extends () => AnyAction & { type: string }>(
  actionCreator: AC
): Matchable<AC>;

// override method to take any action creator with some arguments
export function withMatcher<
  AC extends (...args: any[]) => AnyAction & { type: string }
>(actionCreator: AC): Matchable<AC>;

// implement method to compare the types of this action creator with that
// of the argument action creator
export function withMatcher(actionCreator: Function) {
  const type = actionCreator().type;
  return Object.assign(actionCreator, {
    type,
    match(action: AnyAction) {
      return action.type === type;
    },
  });
}

export type ActionWithPayload<T, P> = {
  type: T;
  payload: P;
};

export type Action<T> = {
  type: T;
};

export function createAction<T extends string, P>(
  type: T,
  payload: P
): ActionWithPayload<T, P>;

export function createAction<T extends string>(type: T, payload: void): Action<T>;

export function createAction<T extends string, P>(type: T, payload: P) {
  return { type, payload };
}
